import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './3SCQuestionnairePageNew.module.css';

// Icons
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);
const CrossIcon = () => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18" /><path d="M6 6l12 12" /></svg>
);

const QuestionnaireStep1 = () => {
    const navigate = useNavigate();

    // State
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const answersRef = React.useRef({});
    const lastSelectionRef = React.useRef(null);
    const [questions, setQuestions] = useState([]);
    // Visible question ids represent the dynamic path taken through the questionnaire.
    const [visibleQuestionIds, setVisibleQuestionIds] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [loading, setLoading] = useState(true);
    const [savingStatus, setSavingStatus] = useState('idle'); // idle | saving | saved | error
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Prevent double fetch in StrictMode/dev by using a ref flag
    const hasFetchedQuestions = React.useRef(false);
    useEffect(() => {
        if (hasFetchedQuestions.current) return;
        hasFetchedQuestions.current = true;
        const fetchQuestionsAndAnswers = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) { navigate('/login'); return; }
                const res = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/threesc/questions/', {
                    method: 'GET',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                if (res.status === 401) { navigate('/login'); return; }
                const data = await res.json();
                const fetchedQuestions = Array.isArray(data) ? data : (data.questions || []);
                setQuestions(fetchedQuestions);

                // Initialize path: start from the question with initial_question=true
                const initial = fetchedQuestions.find(q => q.initial_question) || fetchedQuestions[0];
                if (initial && (!visibleQuestionIds || visibleQuestionIds.length === 0)) {
                    setVisibleQuestionIds([initial.id]);
                }

                // Now fetch all saved answers
                try {
                    const ansRes = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/threesc/fetch-all-answers/', {
                        method: 'GET',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
                    });
                    if (ansRes.ok) {
                        const ansData = await ansRes.json();
                        if (Array.isArray(ansData)) {
                            // Prefill answers
                            const prefill = {};
                            // Map questionId to answer object for quick lookup
                            const answerMap = {};
                            ansData.forEach(a => {
                                if (a.question && a.selected_option) {
                                    prefill[a.question.id] = {
                                        selectedOptionId: a.selected_option.id,
                                        selectedOptionText: a.selected_option.text || '',
                                        score: a.selected_option.score,
                                        note: a.answer_text || ''
                                    };
                                    answerMap[a.question.id] = a;
                                }
                            });
                            setAnswers(prefill);
                            answersRef.current = prefill;

                            // Build visible question path by following the answer chain, but stop at first unanswered
                            let path = [];
                            let q = fetchedQuestions.find(q => q.initial_question) || fetchedQuestions[0];
                            while (q) {
                                path.push(q.id);
                                const ans = prefill[q.id];
                                if (!ans || ans.selectedOptionId == null) break; // stop path at first unanswered
                                // Find the selected option in the question's options
                                const selectedOpt = q.options?.find(o => o.id === ans.selectedOptionId);
                                if (selectedOpt && selectedOpt.redirecting_question && selectedOpt.redirecting_question.id) {
                                    // Find the next question in fetchedQuestions
                                    q = fetchedQuestions.find(qq => qq.id === selectedOpt.redirecting_question.id);
                                } else {
                                    break;
                                }
                            }
                            setVisibleQuestionIds(path);
                            // Set current index to first unanswered question, or last if all answered
                            let firstUnansweredIdx = path.findIndex(qid => !prefill[qid] || prefill[qid].selectedOptionId == null);
                            if (firstUnansweredIdx === -1) firstUnansweredIdx = path.length - 1;
                            setCurrentQuestionIndex(firstUnansweredIdx);
                        }
                    } else {
                        // If error, treat as first time (do nothing)
                    }
                } catch (fetchAnsErr) {
                    // Network or parsing error, treat as first time
                }
            } catch (e) { console.error('Error fetching questions:', e); }
            finally { setLoading(false); }
        };
        fetchQuestionsAndAnswers();
    }, [navigate]);

    // Autosave
    useEffect(() => {
        localStorage.setItem('3sc_questionnaire_progress', JSON.stringify({
            savedAnswers: answers,
            savedIndex: currentQuestionIndex,
            savedPath: visibleQuestionIds
        }));
    }, [answers, currentQuestionIndex, visibleQuestionIds]);

    // Keep totalQuestions in sync with visible questions and clamp current index
    useEffect(() => {
        setTotalQuestions(visibleQuestionIds.length);
        setCurrentQuestionIndex(idx => Math.min(idx, Math.max(0, visibleQuestionIds.length - 1)));
    }, [visibleQuestionIds]);

    if (loading) return <div className={styles.loadingContainer}>Loading questions...</div>;
    if (!questions.length) return <div className={styles.errorContainer}>No questions available.</div>;

    // Use visibleQuestionIds as the displayed order. Map index -> question id -> question object.
    const visibleQuestions = visibleQuestionIds.map(id => questions.find(q => q.id === id)).filter(Boolean);
    const currentQuestion = visibleQuestions[currentQuestionIndex];
    const currentAnswer = answers[currentQuestion?.id] || { selectedOptionId: null, selectedOptionText: '', score: null };
    const currentSelectedOption = currentQuestion?.options?.find(o => o.id === currentAnswer.selectedOptionId);
    const isTerminalNode = !!currentSelectedOption && !currentSelectedOption.redirecting_question?.id;
    const isOptionSelected = currentAnswer.selectedOptionId != null;

    const handleOptionSelect = (option) => {
        if (!currentQuestion) return;
        // Update the answer for the current question
        setAnswers(prev => {
            const updated = {
                ...prev,
                [currentQuestion.id]: {
                    ...prev[currentQuestion.id],
                    selectedOptionId: option.id,
                    selectedOptionText: option.text,
                    score: option.score
                }
            };

            // Rebuild the visible path from the initial question, following the current answers
            let path = [];
            let q = questions.find(q => q.initial_question) || questions[0];
            while (q) {
                path.push(q.id);
                const ans = (q.id === currentQuestion.id)
                    ? { selectedOptionId: option.id } // use the just-selected option for the current question
                    : updated[q.id];
                if (!ans || ans.selectedOptionId == null) break;
                const selectedOpt = q.options?.find(o => o.id === ans.selectedOptionId);
                if (selectedOpt && selectedOpt.redirecting_question && selectedOpt.redirecting_question.id) {
                    q = questions.find(qq => qq.id === selectedOpt.redirecting_question.id);
                } else {
                    break;
                }
            }
            setVisibleQuestionIds(path);

            // Prune answers for questions not in the new path
            const pruned = {};
            for (let qid of path) {
                if (updated[qid]) pruned[qid] = updated[qid];
            }
            answersRef.current = pruned;
            // record last selection so saveAllAnswers can use it immediately
            lastSelectionRef.current = { questionId: currentQuestion.id, answer: pruned[currentQuestion.id] };
            return pruned;
        });
    };
    const handleNoteChange = (e) => {
        setAnswers(prev => ({
            ...prev,
            [currentQuestion.id]: { ...prev[currentQuestion.id] }
        }));
    };
    const handleSubmitAndNext = () => {
        if (isSubmitting) return; // Prevent double submit

        // Determine redirect based on the answer chosen for the current question
        const selectedOption = currentQuestion?.options?.find(o => o.id === currentAnswer.selectedOptionId);
        const redirectToId = selectedOption?.redirecting_question?.id;

        if (redirectToId) {
            // Non-terminal: build the next path deterministically from the selection
            setVisibleQuestionIds(prev => {
                const ids = [...prev];
                const curIdx = currentQuestion ? ids.indexOf(currentQuestion.id) : -1;
                if (curIdx === -1) return ids;
                const nextPath = [...ids.slice(0, curIdx + 1), redirectToId];
                setCurrentQuestionIndex(curIdx + 1);
                return nextPath;
            });
            return;
        }

        // Terminal node: set submitting immediately to avoid race on rapid clicks
        setIsSubmitting(true);

        // Call saveAllAnswers with action and then navigate
        (async () => {
            try {
                await saveAllAnswers('final_submit');
            } catch (e) {
                console.error('Error during final saveAllAnswers:', e);
            } finally {
                navigate('/sustainability/document-upload');
            }
        })();
    };
    const handleSaveAndExit = async () => {
        setSavingStatus('saving');
        try {
            await saveAllAnswers('save_exit');
        } catch (e) {
            console.error('Error during saveAllAnswers (Save & Exit):', e);
        }
        navigate('/');
    };
    const handleSidebarClick = (i) => setCurrentQuestionIndex(i);

    const answeredQuestions = Object.keys(answers).filter(k => answers[k]?.selectedOptionId != null).length;

    // --- Networking helpers ---
    const buildAnswerPayload = (answerEntries) => {
        return {
            answers: answerEntries.map(([questionId, ans]) => ({
                question: questionId,
                selected_option: ans.selectedOptionId,
                score: ans.score
            }))
        };
    };

    const saveSingleAnswer = async (questionId, partial) => {
        const token = localStorage.getItem('token');
        if (!token) return;
        const ans = answers[questionId];
        if (!ans && !partial) return;
        const effective = { ...ans, ...partial };
        if (effective.selectedOptionId == null || effective.score == null) return; // nothing valid yet
        const payload = buildAnswerPayload([[questionId, effective]]);
        try {
            setSavingStatus('saving');
            const res = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/threesc/add-answers/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            if (res.status === 401) { navigate('/login'); return; }
            if (!res.ok) throw new Error('Save failed');
            setSavingStatus('saved');
            setLastSavedAt(new Date());
        } catch (e) {
            console.error('Failed to save answer', e);
            setSavingStatus('error');
        }
    };

    const saveAllAnswers = async (submissionAction = null) => {
        const token = localStorage.getItem('token');
        if (!token) return;
        // Merge any last selection into answersRef immediately to avoid stale state
        if (lastSelectionRef.current) {
            answersRef.current = { ...answersRef.current, [lastSelectionRef.current.questionId]: lastSelectionRef.current.answer };
        }
        // Dynamically compute the current visible path from the latest answers
        let path = [];
        let q = questions.find(q => q.initial_question) || questions[0];
        while (q) {
            path.push(q.id);
            const ans = answersRef.current[q.id];
            if (!ans || ans.selectedOptionId == null) break;
            const selectedOpt = q.options?.find(o => o.id === ans.selectedOptionId);
            if (selectedOpt && selectedOpt.redirecting_question && selectedOpt.redirecting_question.id) {
                q = questions.find(qq => qq.id === selectedOpt.redirecting_question.id);
            } else {
                break;
            }
        }
        // Debug: show merged state before building payload
        console.debug('saveAllAnswers: lastSelectionRef:', lastSelectionRef.current);
        console.debug('saveAllAnswers: answersRef before path:', answersRef.current);
        // Only include answers for questions in the current path, up to the first unanswered
        let validEntries = [];
        for (let qid of path) {
            const a = answersRef.current[qid];
            if (!a || a.selectedOptionId == null || a.score == null) break;
            validEntries.push([qid, a]);
        }
        if (!validEntries.length) return;
        const payload = buildAnswerPayload(validEntries);
        console.debug('saveAllAnswers: computed path:', path);
        console.debug('saveAllAnswers: payload:', payload);
        if (submissionAction) {
            payload.submission_action = submissionAction;
        }
        try {
            setSavingStatus('saving');
            console.debug('saveAllAnswers: sending payload', payload);
            const res = await fetch(process.env.REACT_APP_API_BASE_URL + '/api/threesc/answers/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            console.debug('saveAllAnswers: received response', res.status);
            if (res.status === 401) { navigate('/login'); return; }
            if (!res.ok) throw new Error('Bulk save failed');
            setSavingStatus('saved');
            setLastSavedAt(new Date());
            // clear last selection after successful save
            lastSelectionRef.current = null;
        } catch (e) {
            console.error('Failed to bulk save answers', e);
            setSavingStatus('error');
            throw e;
        }
    };

    return (
        <div className={styles.questionnairePage}>
            <div className={styles.header}>
                <h1>Welcome to Step 1 of your 3SC journey.</h1>
                <div className={styles.saveStatus}>
                    {savingStatus === 'saving' && <span>Saving...</span>}
                    {savingStatus === 'saved' && lastSavedAt && <span>Saved at {lastSavedAt.toLocaleTimeString()}</span>}
                    {savingStatus === 'error' && <span className={styles.errorText}>Save failed (retry on next change)</span>}
                </div>
            </div>
            <main className={styles.mainContent}>
                <aside className={styles.sidebar}>
                    <h2>Questions ({answeredQuestions}/{visibleQuestions.length})</h2>
                    <div className={styles.sidebarList}>
                        {visibleQuestions.map((q, i) => {
                            const isAnswered = answers[q.id]?.selectedOptionId != null;
                            return (
                                <div key={q.id} className={`${styles.sidebarItem} ${i === currentQuestionIndex ? styles.activeItem : ''}`} onClick={() => handleSidebarClick(i)} title={q.text}>
                                    <span className={styles.sidebarStatus}>
                                        {isAnswered ? <span className={styles.tick}><CheckIcon /></span> : <span className={styles.cross}><CrossIcon /></span>}
                                    </span>
                                    Question {i + 1}
                                </div>
                            );
                        })}
                     </div>
                </aside>
                <div className={styles.questionCard}>
                    <div className={styles.questionHeader}>
                        <span className={styles.questionCounter}>Question {currentQuestionIndex + 1}</span>
                        <p className={styles.questionText}>{currentQuestion?.text || ''}</p>
                    </div>
                    <div className={styles.mcqOptions}>
                                                {currentQuestion.options?.map((option) => {
                                                        const isSelected = currentAnswer.selectedOptionId === option.id;
                                                        return (
                                                                <button
                                                                    key={option.id}
                                                                    className={`${styles.optionButton} ${isSelected ? styles.selected : ''}`}
                                                                    onClick={() => handleOptionSelect(option)}
                                                                >
                                                                    {option.text}
                                                                </button>
                                                        );
                                                })}
                    </div>
                    <div className={styles.navigationButtons}>
                        <button className={styles.secondaryButton} onClick={handleSaveAndExit}>Save & Exit</button>
                        <button className={styles.primaryButton} onClick={handleSubmitAndNext} disabled={!isOptionSelected || isSubmitting}>
                            {isTerminalNode ? 'Finish & Submit' : 'Submit & Next'}
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default QuestionnaireStep1;